import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {AuthService} from '../../core/security/auth.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {debounceTime} from 'rxjs/operators';
import {PhoneNumberGroupDatasource} from './phone-number-group.datasource';
import { MessagingService } from '../messaging.service';
import { MessagingGroupModel } from '../../api/models/messaging-group-model';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatSelectionList, MatListOption } from '@angular/material/list';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { PhoneNumberGroupItemComponent } from '../phone-number-group-item/phone-number-group-item.component';

@Component({
    selector: 'app-phone-number-group',
    template: `
    <div class="thread-sort">
      <mat-button-toggle-group name="sortType" [(ngModel)]="sortType" (ngModelChange)="sortChanged($event)" aria-label="Sort Type">
        <mat-button-toggle matTooltip="Sort by date" value="sortByDate">
          <mat-icon>sort</mat-icon>
        </mat-button-toggle>
        <mat-button-toggle matTooltip="Sort by unread and date" value="sortByDateAndUnread">
          <mat-icon>segment</mat-icon>
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>
    <mat-selection-list class="thread-list" [multiple]="false" [hideSingleSelectionIndicator]="true">
      <cdk-virtual-scroll-viewport itemSize="99" class="thread-viewport">
        <mat-list-option *cdkVirtualFor="let item of dataSource"
                         [selected]="item?.threadKey === selectedTheadKey"
                         (click)="onRowClick($event, item)">
          <app-phone-number-group-item
            [dataItem]="item"
            [hasContactsModule]="hasContactsModule"
            [hasContactsEditRight]="hasContactsEditRight"
          ></app-phone-number-group-item>
        </mat-list-option>
      </cdk-virtual-scroll-viewport>
    </mat-selection-list>

  `,
    styles: [],
    standalone: true,
    imports: [MatButtonToggleGroup, FormsModule, MatButtonToggle, MatTooltip, MatIcon, MatSelectionList, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf, MatListOption, PhoneNumberGroupItemComponent]
})
export class PhoneNumberGroupComponent implements OnInit, OnDestroy, OnChanges {
  private readonly TAG = '[PhoneNumberGroupComponent]';
  @Input() selectedNumber: string;
  @Input() selectedTheadKey: string;
  @Input() hasContactsModule = false;
  @Input() hasContactsEditRight = false;
  @Input() defaultSortType: 'sortByDateAndUnread' | 'sortByDate' | string = 'sortByDateAndUnread';
  @Output() selectedThreadChangeEvent = new EventEmitter<MessagingGroupModel>();

  onWebSocketConnectSubscription: Subscription;
  dataSourceSubject = new Subject();
  dataSourceSubscription: Subscription;

  sortType: 'sortByDateAndUnread' | 'sortByDate' | string = 'sortByDateAndUnread';

  constructor(public messagingService: MessagingService,
              public authService: AuthService,
              public dataSource: PhoneNumberGroupDatasource,
              private router: Router,
              public dialog: MatDialog,
  ) {
    this.dataSource.selectedThreadChangeEvent.subscribe(data => {
      console.log(`${this.TAG} selectedThreadChangeEvent`, data);
      this.selectedThreadChangeEvent.emit(data);
    });
  }

  ngOnInit() {
    console.log(`${this.TAG} init`);

    this.onWebSocketConnectSubscription = this.messagingService.onWebSocketConnectSubject.subscribe(() => {
      this.dataSourceSubject.next('');
    });

    this.dataSourceSubscription = this.dataSourceSubject.pipe(debounceTime(100)).subscribe(() => {
      this.filterData();
    });

    this.filterData();
  }

  filterData() {
    this.dataSource.onFilterDataChanged({
      phoneNumber: this.selectedNumber,
      sortType: this.sortType,
      threadKey: this.selectedTheadKey,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(`${this.TAG} ngOnChanges`, changes);
    if (changes['selectedNumber'] || changes['searchText']) {
      this.dataSourceSubject.next('');
    }

    if (changes['selectedTheadKey'] && !changes['selectedTheadKey'].isFirstChange()) {
      if (this.dataSource) {
        this.dataSource.onThreadKeyChanged(this.selectedTheadKey);
      }
    }

    if (changes['defaultSortType']) {
      this.sortType = this.defaultSortType;
    }
  }

  async forceRefresh() {
    if (this.dataSource) {
      await this.dataSource.resetData();
    }
  }

  onRowClick(event, dataItem) {
    console.log(`${this.TAG} onRowClick`, dataItem);
    this.messagingService.isComposing = false;

    this.router
      .navigate([], { queryParams: { tn: this.selectedNumber, thread: dataItem.threadKey } })
      .catch(error => console.log(`${this.TAG} route`, error));

    this.markUnreadMessages(dataItem).catch(error => console.error(`${this.TAG} markUnreadMessages`, error));
  }

  async markUnreadMessages(group) {
    console.log(`${this.TAG} mark unread messages`);

    if (group && group.unread > 0) {
      await this.messagingService.markUnreadMessages(group);
    }
  }

  ngOnDestroy() {
    this.onWebSocketConnectSubscription?.unsubscribe();
    this.dataSourceSubscription?.unsubscribe();
  }

  sortChanged($event: any) {
    console.log(`${this.TAG} sortChanged`);
    this.authService.saveUiSettings({
      smsThreadSortType: $event,
    });
    this.dataSource.onSortTypeChanged($event);
  }
}
